/*
 * Copyright © 2021 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschützt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulässig. Dies gilt
 * insbesondere für Vervielfältigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht übergeben, ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 */

import {HttpErrorResponse} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {TempBlob} from '@dv/kitadmin/models';
import {ReportBlobApi} from '@dv/shared/backend/api/report-blob.api';
import {ReportGenerationStatus} from '@dv/shared/backend/model/report-generation-status';
import {addError$, DvbError, ERROR_CODE, ErrorLevel, ErrorType} from '@dv/shared/code';
import {catchError, first, interval, map, Observable, switchMap, take} from 'rxjs';

const transactionTimeoutMinutes = 20;
const secondsPerMin = 60;
const milliesPerSecond = 1000;
const maxDurationInMillies = transactionTimeoutMinutes * secondsPerMin * milliesPerSecond;
const pollingIntervalMs = 10_000;
const pollingMaxAttempts = maxDurationInMillies / pollingIntervalMs;

export interface PollingConfig {
    pollingIntervalMillies: number;
    pollingMaxAttempts: number;
}

@Injectable({
    providedIn: 'root',
})
export class ReportPollingService {

    private readonly reportBlobApi = inject(ReportBlobApi);

    public startPolling(reportBlobId: string, config?: PollingConfig): Observable<TempBlob> {
        return interval(config?.pollingIntervalMillies ?? pollingIntervalMs).pipe(
            take(config?.pollingMaxAttempts ?? pollingMaxAttempts),
            switchMap(() => this.reportBlobApi.getReportBlobStatus$({reportBlobId})),
            first(response => response.status === ReportGenerationStatus.FINISHED),
            catchError((error: unknown) => {
                if (error instanceof HttpErrorResponse) {
                    // handled by interceptor
                    // eslint-disable-next-line @typescript-eslint/only-throw-error
                    throw error;
                }

                // notify user
                addError$.next(new DvbError(ErrorType.INTERNAL,
                    ErrorLevel.SEVERE,
                    'ERRORS.ERR_REPORT_POLLING',
                    ERROR_CODE.WRAPPED_ERROR,
                    {error}));

                throw new Error('polling exhausted');
            }),
            map(response => TempBlob.apiResponseTransformer(response.tempBlob)),
        );
    }
}
